/**
 * Froala Mention Plugin
 * Adds @mention functionality to Froala Editor instances that have mentionEnabled: true.
 *
 * Expected Froala config options:
 *   mentionEnabled: boolean
 *   mentionSearchUrl: string   – base URL (searchText param is appended)
 */

function mentionPluginFactory(editor: any) {
  // ── Guard ────────────────────────────────────────────────────────────
  if (!editor.opts.mentionEnabled) {
    return { _init() {}, destroy() {} };
  }

  // ── State ────────────────────────────────────────────────────────────
  let dropdown: HTMLDivElement | null = null;
  let items: any[] = [];
  let selectedIndex = 0;
  let debounceTimer: any = null;
  let isOpen = false;
  let currentSearchTerm = '';

  // ── Helpers ──────────────────────────────────────────────────────────

  function getTextBeforeCursor(): string {
    const sel = editor.selection.get();
    if (!sel || sel.rangeCount === 0) return '';
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return '';

    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return '';
    return node.textContent?.substring(0, range.startOffset) ?? '';
  }

  function getMentionMatch(): RegExpExecArray | null {
    const text = getTextBeforeCursor();
    // Match @ at start of text or after whitespace, followed by search chars
    const regex = /(^|[\s])@([^\s@]*)$/;
    return regex.exec(text);
  }

  // ── Dropdown ─────────────────────────────────────────────────────────

  function createDropdown(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'fr-mention-dropdown';
    // Animação slide-up leve: começa 8px abaixo com opacity 0; openDropdown()
    // dispara transição para posição final após o rAF inicial.
    el.style.cssText = `
      position: fixed;
      z-index: 99999;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12);
      max-height: 240px;
      overflow-y: auto;
      min-width: 220px;
      max-width: 320px;
      display: none;
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 150ms ease-out, transform 150ms ease-out;
    `;
    document.body.appendChild(el);
    return el;
  }

  function positionDropdown(): void {
    if (!dropdown) return;
    const sel = editor.selection.get();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // With position:fixed, use viewport coordinates directly
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.top = `${rect.bottom + 4}px`;
  }

  function renderItems(): void {
    if (!dropdown) return;
    if (items.length === 0) {
      closeDropdown();
      return;
    }

    dropdown.innerHTML = items.map((user, i) => {
      const displayName = user.socialName || user.name || user.username || '';
      const username = user.username || '';
      const photo = user.photoS3 || user.photoS3aws || '';
      const imageFile = user.image || '';
      const avatarUrl = photo || (imageFile && !imageFile.includes('default_user') ? `https://files.mybeehome.com/images/small/${imageFile}` : '');
      const initial = displayName.charAt(0).toUpperCase();
      const isActive = i === selectedIndex;

      const avatarHtml = avatarUrl
        ? `<img src="${avatarUrl}" style="width:32px;height:32px;border-radius:50%;object-fit:cover;flex-shrink:0;" alt="">`
        : `<div style="width:32px;height:32px;border-radius:50%;background:#1B72E8;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;flex-shrink:0;">${initial}</div>`;

      return `
        <div class="fr-mention-item" data-index="${i}"
             style="display:flex;align-items:center;gap:8px;padding:8px 12px;cursor:pointer;
                    background:${isActive ? '#f0f4ff' : '#fff'};">
          ${avatarHtml}
          <div style="font-size:14px;font-weight:500;color:#1a1a1a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ${displayName}
          </div>
        </div>
      `;
    }).join('');

    dropdown.style.display = 'block';
    isOpen = true;

    // Attach click handlers
    dropdown.querySelectorAll('.fr-mention-item').forEach((el: Element) => {
      el.addEventListener('mousedown', (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        const idx = parseInt((el as HTMLElement).getAttribute('data-index') || '0', 10);
        selectItem(idx);
      });
    });

    // Scroll active item into view
    const activeEl = dropdown.querySelector(`[data-index="${selectedIndex}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }

  function openDropdown(): void {
    if (!dropdown) {
      dropdown = createDropdown();
    }
    positionDropdown();
    renderItems();
    // Dispara transição slide-up/fade-in no próximo frame (depois que display:block).
    requestAnimationFrame(() => {
      if (dropdown) {
        dropdown.style.opacity = '1';
        dropdown.style.transform = 'translateY(0)';
      }
    });
  }

  function closeDropdown(): void {
    if (dropdown) {
      dropdown.style.display = 'none';
      // Reset para próxima abertura voltar do estado inicial animado.
      dropdown.style.opacity = '0';
      dropdown.style.transform = 'translateY(8px)';
    }
    isOpen = false;
    items = [];
    selectedIndex = 0;
    currentSearchTerm = '';
  }

  // ── Search ───────────────────────────────────────────────────────────

  function fetchUsers(term: string): void {
    const url = editor.opts.mentionSearchUrl + '&searchText=' + encodeURIComponent(term);

    fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then(res => {
        if (!res.ok) throw new Error('Network error');
        return res.json();
      })
      .then(data => {
        // The current search term may have changed while we were fetching
        if (currentSearchTerm !== term) return;

        items = Array.isArray(data) ? data : [];
        selectedIndex = 0;
        if (items.length > 0) {
          openDropdown();
        } else {
          closeDropdown();
        }
      })
      .catch(() => {
        closeDropdown();
      });
  }

  function debouncedSearch(term: string): void {
    currentSearchTerm = term;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetchUsers(term);
    }, 300);
  }

  // ── Insertion ────────────────────────────────────────────────────────

  function selectItem(index: number): void {
    const user = items[index];
    if (!user) return;

    const username = user.username || '';
    const displayName = user.socialName || user.name || username;
    const userId = user.id || '';

    // Remove the @searchTerm from the text
    const sel = editor.selection.get();
    if (!sel || sel.rangeCount === 0) { closeDropdown(); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;

    if (node.nodeType === Node.TEXT_NODE) {
      const textBefore = node.textContent?.substring(0, range.startOffset) ?? '';
      const match = /(^|[\s])@([^\s@]*)$/.exec(textBefore);
      if (match) {
        const matchStart = match.index + match[1].length; // start of @
        const matchEnd = range.startOffset;

        // Delete the @searchTerm
        range.setStart(node, matchStart);
        range.setEnd(node, matchEnd);
        range.deleteContents();

        // Insert the mention span
        const mentionSpan = document.createElement('span');
        mentionSpan.className = 'fr-mention';
        mentionSpan.setAttribute('data-mention-id', String(userId));
        mentionSpan.setAttribute('data-mention-username', username);
        mentionSpan.contentEditable = 'false';
        mentionSpan.style.color = '#1B72E8';
        mentionSpan.style.fontWeight = '500';
        mentionSpan.textContent = `@${username}`;

        range.insertNode(mentionSpan);

        // Add a non-breaking space after the mention so the user can continue typing
        const nbsp = document.createTextNode('\u00A0');
        mentionSpan.parentNode?.insertBefore(nbsp, mentionSpan.nextSibling);

        // Move cursor after the nbsp
        const newRange = document.createRange();
        newRange.setStartAfter(nbsp);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    }

    closeDropdown();
    editor.events.trigger('contentChanged');
    editor.undo.saveStep();
  }

  // ── Event Handlers ───────────────────────────────────────────────────

  function onKeyup(e: KeyboardEvent): void {
    // Skip navigation keys handled in keydown
    if (['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;

    const match = getMentionMatch();
    if (match) {
      const term = match[2]; // text after @
      if (term.length >= 0) {
        debouncedSearch(term);
      }
    } else {
      closeDropdown();
    }
  }

  function onKeydown(e: KeyboardEvent): boolean | void {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        selectedIndex = (selectedIndex + 1) % items.length;
        renderItems();
        return false;

      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        selectedIndex = (selectedIndex - 1 + items.length) % items.length;
        renderItems();
        return false;

      case 'Enter':
      case 'Tab':
        e.preventDefault();
        e.stopPropagation();
        selectItem(selectedIndex);
        return false;

      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        closeDropdown();
        return false;
    }
  }

  function onClickOutside(e: MouseEvent): void {
    if (dropdown && !dropdown.contains(e.target as Node)) {
      closeDropdown();
    }
  }

  function onBlur(): void {
    // Delay to allow click events on dropdown items to fire first
    setTimeout(() => {
      closeDropdown();
    }, 200);
  }

  function onScroll(): void {
    if (isOpen) {
      closeDropdown();
    }
  }

  // ── Plugin lifecycle ─────────────────────────────────────────────────

  function _init(): void {
    editor.events.on('keyup', onKeyup, true);
    editor.events.on('keydown', onKeydown, true);
    document.addEventListener('click', onClickOutside);
    window.addEventListener('scroll', onScroll, true); // true = capture, catches scroll on any element
    editor.events.on('blur', onBlur);
  }

  function destroy(): void {
    if (debounceTimer) clearTimeout(debounceTimer);
    document.removeEventListener('click', onClickOutside);
    window.removeEventListener('scroll', onScroll, true);
    if (dropdown) {
      dropdown.remove();
      dropdown = null;
    }
    isOpen = false;
  }

  return { _init, destroy };
}

// Register plugin on load — works with both static and dynamic imports of FroalaEditor
export function registerMentionPlugin(FroalaEditor: any): void {
  if (FroalaEditor && FroalaEditor.PLUGINS) {
    FroalaEditor.PLUGINS.mention = mentionPluginFactory;
  }
}

// Auto-register: dynamically import FroalaEditor and attach the plugin
import('froala-editor/js/froala_editor.pkgd.min.js').then((mod) => {
  registerMentionPlugin(mod.default || mod);
});
